import React from "react";
// COMPONENTS
import SiteSectionHead from "Components/WebsiteTheme/SiteSectionHead";
import { ToneIcon } from "Components/WebsiteTheme/SiteIcons";
import { revealProps } from "Components/WebsiteTheme/Reveal";
// STYLES
import { ShowcaseSection } from "./LandingPage.style";
// IMAGES
import PeriodicTableScreen from "Assets/Images/ClassroomPilot/periodic-table-screen.webp";

const FONT = "Outfit, sans-serif";

/* Teacher Portal: "Create Assessment" with AI question generation (drawn, so it stays sharp at any size). */
const CreateScreen = () => (
	<svg viewBox="0 0 480 300" aria-hidden="true" focusable="false">
		<rect width="480" height="300" fill="#f6f8fd" />
		<rect width="64" height="300" fill="#142a6e" />
		<rect x="14" y="16" width="36" height="8" rx="4" fill="#ffffff" opacity="0.9" />
		{[52, 80, 108, 136, 164].map((y, i) => (
			<rect key={y} x="14" y={y} width="36" height="14" rx="4" fill={i === 1 ? "#1f63e0" : "#ffffff"} opacity={i === 1 ? 1 : 0.18} />
		))}
		<rect x="64" width="416" height="40" fill="#ffffff" />
		<text x="84" y="25" fontFamily={FONT} fontSize="14" fontWeight="700" fill="#142a6e">
			Create Assessment
		</text>
		<circle cx="452" cy="20" r="10" fill="#edf3ff" />
		<rect x="84" y="56" width="372" height="226" rx="10" fill="#ffffff" stroke="#e3e8f3" />
		<rect x="100" y="70" width="104" height="24" rx="12" fill="#ffffff" stroke="#e3e8f3" />
		<text x="152" y="86" textAnchor="middle" fontFamily={FONT} fontSize="10" fontWeight="600" fill="#5f6987">
			Type Questions
		</text>
		<rect x="212" y="70" width="140" height="24" rx="12" fill="#1f63e0" />
		<text x="282" y="86" textAnchor="middle" fontFamily={FONT} fontSize="10" fontWeight="700" fill="#ffffff">
			AI Generate Questions
		</text>
		<text x="100" y="118" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#142a6e">
			Topic or keyword
		</text>
		<rect x="100" y="125" width="340" height="28" rx="6" fill="#f6f8fd" stroke="#d3e1fb" />
		<text x="110" y="143" fontFamily={FONT} fontSize="10" fill="#414b6b">
			The three branches of government
		</text>
		<text x="100" y="172" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#142a6e">
			Subject
		</text>
		<rect x="100" y="179" width="164" height="28" rx="6" fill="#f6f8fd" stroke="#d3e1fb" />
		<circle cx="114" cy="193" r="5" fill="#d42f32" />
		<text x="124" y="197" fontFamily={FONT} fontSize="10" fill="#414b6b">
			Civics
		</text>
		<path d="M248 190l4 4 4-4" stroke="#5f6987" strokeWidth="1.6" fill="none" strokeLinecap="round" />
		<text x="276" y="172" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#142a6e">
			Questions
		</text>
		<rect x="276" y="179" width="164" height="28" rx="6" fill="#f6f8fd" stroke="#d3e1fb" />
		<text x="290" y="197" fontFamily={FONT} fontSize="10" fill="#414b6b">
			10 questions
		</text>
		<path d="M424 190l4 4 4-4" stroke="#5f6987" strokeWidth="1.6" fill="none" strokeLinecap="round" />
		<rect x="300" y="234" width="140" height="30" rx="8" fill="#d9480f" />
		<text x="370" y="253" textAnchor="middle" fontFamily={FONT} fontSize="10.5" fontWeight="700" fill="#ffffff">
			Generate Questions
		</text>
	</svg>
);

const RINGS = [
	{ label: "Civics", value: 86, color: "#16a34a" },
	{ label: "History", value: 72, color: "#1f63e0" },
	{ label: "Science", value: 64, color: "#7a4ee6" },
	{ label: "Economics", value: 91, color: "#f26b21" },
];

/* Teacher dashboard: class progress by subject and the most missed questions. */
const TrackScreen = () => {
	const radius = 24;
	const circumference = 2 * Math.PI * radius;
	return (
		<svg viewBox="0 0 480 300" aria-hidden="true" focusable="false">
			<rect width="480" height="300" fill="#f6f8fd" />
			<rect width="64" height="300" fill="#142a6e" />
			<rect x="14" y="16" width="36" height="8" rx="4" fill="#ffffff" opacity="0.9" />
			{[52, 80, 108, 136, 164].map((y, i) => (
				<rect key={y} x="14" y={y} width="36" height="14" rx="4" fill={i === 3 ? "#1f63e0" : "#ffffff"} opacity={i === 3 ? 1 : 0.18} />
			))}
			<rect x="64" width="416" height="40" fill="#ffffff" />
			<text x="84" y="25" fontFamily={FONT} fontSize="14" fontWeight="700" fill="#142a6e">
				Class Performance
			</text>
			<rect x="380" y="11" width="80" height="18" rx="9" fill="#edf3ff" />
			<text x="420" y="24" textAnchor="middle" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#1f63e0">
				This week
			</text>
			{RINGS.map((ring, i) => {
				const x = 84 + i * 94;
				return (
					<g key={ring.label}>
						<rect x={x} y="54" width="84" height="100" rx="10" fill="#ffffff" stroke="#e3e8f3" />
						<circle cx={x + 42} cy="92" r={radius} fill="none" stroke="#eef1f7" strokeWidth="7" />
						<circle
							cx={x + 42}
							cy="92"
							r={radius}
							fill="none"
							stroke={ring.color}
							strokeWidth="7"
							strokeLinecap="round"
							strokeDasharray={`${(circumference * ring.value) / 100} ${circumference}`}
							transform={`rotate(-90 ${x + 42} 92)`}
						/>
						<text x={x + 42} y="96" textAnchor="middle" fontFamily={FONT} fontSize="11" fontWeight="700" fill="#142a6e">
							{ring.value}%
						</text>
						<text x={x + 42} y="138" textAnchor="middle" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#5f6987">
							{ring.label}
						</text>
					</g>
				);
			})}
			<rect x="84" y="168" width="366" height="116" rx="10" fill="#ffffff" stroke="#e3e8f3" />
			<text x="100" y="190" fontFamily={FONT} fontSize="10.5" fontWeight="700" fill="#142a6e">
				Areas to reinforce
			</text>
			{[
				{ label: "Checks and balances", w: 170 },
				{ label: "Supply and demand", w: 132 },
				{ label: "Cell structure", w: 96 },
			].map((row, i) => (
				<g key={row.label}>
					<text x="100" y={214 + i * 24} fontFamily={FONT} fontSize="9.5" fill="#414b6b">
						{row.label}
					</text>
					<rect x="226" y={206 + i * 24} width="200" height="9" rx="4.5" fill="#eef1f7" />
					<rect x="226" y={206 + i * 24} width={row.w} height="9" rx="4.5" fill="#f26b21" />
				</g>
			))}
		</svg>
	);
};

const ITEMS = [
	{ key: "create", tone: "blue", title: "Create", text: "Build assessments your way.", screen: <CreateScreen />, label: "Create Assessment screen with AI question generation" },
	{
		key: "engage",
		tone: "green",
		title: "Engage",
		text: "Turn learning into interactive challenges.",
		screen: <img src={PeriodicTableScreen} alt="" width="960" height="600" loading="lazy" />,
		label: "The Science Master Interactive Periodic Table game",
	},
	{ key: "track", tone: "purple", title: "Track", text: "See performance and identify gaps.", screen: <TrackScreen />, label: "Class performance dashboard" },
];

/** Designed for the Classroom: Create, Engage and Track, each on a laptop screen. */
const ClassroomShowcase = () => (
	<ShowcaseSection className="sm_section sm_section--tint">
		<div className="sm_container">
			<SiteSectionHead
				{...revealProps("up")}
				align="center"
				title="Designed for the Classroom."
				lead="From creating questions to seeing what students understand, all in one learning experience."
			/>

			<ul className="cs_grid">
				{ITEMS.map((item, index) => (
					<li key={item.key} className="cs_item" {...revealProps("up", index)}>
						<figure className="cs_laptop" role="img" aria-label={item.label}>
							<div className="cs_screen">{item.screen}</div>
							<div className="cs_base" />
						</figure>
						<div className="cs_copy">
							<ToneIcon iconKey={item.key} tone={item.tone} />
							<h3 className="sm_h4">{item.title}</h3>
							<p className="sm_small">{item.text}</p>
						</div>
					</li>
				))}
			</ul>
		</div>
	</ShowcaseSection>
);

export default ClassroomShowcase;
